import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BehaviorSubject, Observable, of } from 'rxjs';

import { RequestResultStatusCodeEnum } from 'src/app/Enums/RequestResultStatusCodeEnum';
import { Nfce } from 'src/app/class/NfceWizard/Nfce.class';
import { AccountantNfce } from 'src/app/models/NfceWizard/Accountant';
import { CertificateA1 } from 'src/app/models/NfceWizard/CertificateA1';
import { CompanyNfce } from 'src/app/models/NfceWizard/Company';
import { CompanyNfceInformation } from 'src/app/models/NfceWizard/CompanyNfceInformation';
import { NfceInformation } from 'src/app/models/NfceWizard/NfceInformation';
import { PdvNfceInformation } from 'src/app/models/NfceWizard/PdvNfceInformation';
import { TaxRegime } from 'src/app/models/NfceWizard/TaxRegime.model';
import { ApiService, ErrorResponse } from '../../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class WizardNfceService {
  private _nfce: Nfce = new Nfce();
  get nfce(): Nfce {
    return this._nfce;
  }

  private _isCompanyDetailSet: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isAccountantDetailSet: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  setCompanyDetail(company: CompanyNfce) {
    this._nfce.SetCompanyData(company);
    this._isCompanyDetailSet.next(!company);
  }

  getCompanyDetail(): Observable<boolean> {
    return this._isCompanyDetailSet.asObservable();
  }

  setAccountantDetail(accountant: AccountantNfce) {
    this._nfce.SetAccountantData(accountant);
    this._isAccountantDetailSet.next(!accountant);
  }

  getAccountantDetail(): Observable<boolean> {
    return this._isAccountantDetailSet.asObservable();
  }

  private _isCertificateValidDateSet: BehaviorSubject<Date | null> = new BehaviorSubject<Date | null>(null);
  setCertificataValidity(certificateA1ValidUntil: Date | null) {
    this.setCertificateA1(certificateA1ValidUntil);
    this._isCertificateValidDateSet.next(certificateA1ValidUntil);
  }

  getCertificateValidUntil(): Observable<Date | null> {
    return this._isCertificateValidDateSet.asObservable();
  }

  constructor(private api: ApiService, private snackBar: MatSnackBar,) { }

  private _valuesLoading: Promise<void> | null = null;

  public setValues(): Promise<void> {
    this._nfce.CleanClass();
    this._valuesLoading = this.getNfce()
      .then(r => {
        this._nfce.SetTaxRegime(r.GetTaxRegime() as TaxRegime);
        this.setCompanyDetail(r.GetCompanyData() as CompanyNfce)
        r.GetAccountantsData()?.forEach(acc => {this.setAccountantDetail(acc);});
        this.setCertificataValidity(r.GetCompanyCertificateA1() as Date)
        this._nfce.SetCompanyNfceInformation(r.GetCompanyNfceInformation() as NfceInformation);
      })
      .catch(() => this.alertWarningMessage("Erro ao preencher informações do Wizard"));
    return this._valuesLoading;
  }

  // Permite que as telas aguardem o retorno da API antes de ler os valores (evita ler o default do CleanClass).
  public waitValues(): Promise<void> {
    return this._valuesLoading ?? Promise.resolve();
  }

  async setTaxRegimeMEI(): Promise<boolean> {
    this._nfce.SetTaxRegimeMEI();
    return await this.sendTaxRegime(this._nfce.GetTaxRegime() ?? {} as TaxRegime);
  }

  async setTaxRegimeSimplesNacional(option: 1 | 2): Promise<boolean> {
    this._nfce.SetTaxRegimeSimplesNacional(option);
    return await this.sendTaxRegime(this._nfce.GetTaxRegime() ?? {} as TaxRegime);
  }

  async setCompanyDetails(company: CompanyNfce, tokenOverride?: string): Promise<boolean> {
    this._nfce.SetCompanyData(company)
    return await this.sendCompanyInformation(this.getCompanyDetails(), tokenOverride);
  }
  getCompanyDetails(): CompanyNfce {
    return this._nfce.GetCompanyData() ?? {} as CompanyNfce;
  }

  async setAccountantDetails(accountant: AccountantNfce): Promise<boolean> {
    return await this.sendAccountantInformation(accountant);
  }

  getAccountants(): AccountantNfce[] {
    return this._nfce.GetAccountantsData();
  }

  getAccountantDetails(accountant: AccountantNfce): AccountantNfce | null {
    return this._nfce.GetAccountantByEntity(accountant);
  }

  setCertificateA1(sent: Date | null) {
    this._nfce.SetCompanyCertificateA1(sent);
  }
  getCertificateA1(): Date | null {
    return this._nfce.GetCompanyCertificateA1();
  }

  async SetCompanyNfceInformation(nfceInformation: NfceInformation): Promise<boolean> {
    this._nfce.SetCompanyNfceInformation(nfceInformation);
    return await this.sendNfceInformation(this.GetCompanyNfceInformation() ?? {} as NfceInformation);
  }

  async UpdateCompanyNfceInformation(companyNfceInformation: CompanyNfceInformation, tokenOverride?: string): Promise<boolean> {
    let companyInformation: NfceInformation = this._nfce.GetCompanyNfceInformation() ?? {} as NfceInformation;
    companyInformation.csc = companyNfceInformation?.csc ?? "";
    companyInformation.cscId = companyNfceInformation?.cscId ?? 0;
    companyInformation.environment = companyNfceInformation.environment;
    this._nfce.SetCompanyNfceInformation(companyInformation);
    return await this.sendCompanyNfceInformation(companyNfceInformation, tokenOverride);
  }

  async SetCompanyNfceInformationOutOfWizard(pdvNfceInformation: PdvNfceInformation, tokenOverride?: string) {
    await this.sendNfceInformationOutOfWizard(pdvNfceInformation, tokenOverride);
  }

  GetCompanyNfceInformation(): NfceInformation | null {
    return this._nfce.GetCompanyNfceInformation();
  }

  async sendCertificate(certificate: CertificateA1, tokenOverride?: string): Promise<Date | null> {
    const formData = new FormData();
    formData.append('file', certificate.file);
    formData.append('password', certificate.password);
    const r = await this.api.post<any>('/v1/nfce/certificate', formData, tokenOverride);

    if (!r.success && r.message) {
      this.alertWarningMessage(r.message.toString())
      this.setCertificataValidity(null);
      return null;
    }

    this.setCertificataValidity(r.data);
    return r.data;
  }

  private async sendTaxRegime(taxRegime: TaxRegime): Promise<boolean> {
    const r = await this.api.post<TaxRegime>('/v1/nfce/tax-regime', taxRegime);
    if (!r.success && r.data) {
      this.alertWarningMessage(r.data.toString());
    }
    return r.success;
  }

  private async sendCompanyInformation(company: CompanyNfce, tokenOverride?: string): Promise<boolean> {
    const r = await this.api.post<CompanyNfce>('/v1/nfce/company-information', company, tokenOverride);
    if (r.statusCode == RequestResultStatusCodeEnum.BadRequest && r.data) {
      let message = "";
      const data = r.data as ErrorResponse[];

      data.forEach((messageApi: any) => {
        message += messageApi.message + " \n";
      })

      this.alertWarningMessage(message);
      return r.success;
    }
    if (!r.success && r.data) {
      this.alertWarningMessage(r.data.toString())
    }
    return r.success;
  }

  async copyLinkAccess(accountant: AccountantNfce): Promise<{data?: any, success: boolean}> {
    const r = await this.api.post<AccountantNfce>('/v1/nfce/accountant-information', accountant);
    if (r.statusCode == RequestResultStatusCodeEnum.BadRequest && r.data) {
      let message = "";
      const data = r.data as ErrorResponse[];

      data.forEach((messageApi: any) => {
        message += messageApi.message + " \n";
      })

      this.alertWarningMessage(message);
      return {data: r.data, success: r.success};
    }
    if (!r.success && r.data) {
      this.alertWarningMessage(r.data.toString())
    }
    if(accountant.copyLink){
      return {data: r.data, success: r.success};
    }
    return {data: r.data, success: r.success};
  }


  private async sendAccountantInformation(accountant: AccountantNfce): Promise<boolean> {
    const r = await this.api.post<AccountantNfce>('/v1/nfce/accountant-information', accountant);
    if (r.statusCode == RequestResultStatusCodeEnum.BadRequest && r.data) {
      let message = "";
      const data = r.data as ErrorResponse[];

      data.forEach((messageApi: any) => {
        message += messageApi.message + " \n";
      });

      this.alertWarningMessage(message);
      return r.success;
    }

    if (!r.success && r.data) this.alertWarningMessage(r.data.toString());
    return r.success;
  }

  private async sendNfceInformation(nfceInformation: NfceInformation): Promise<boolean> {
    const r = await this.api.post<NfceInformation>('/v1/nfce/nfce-information', nfceInformation);
    if (r.statusCode == RequestResultStatusCodeEnum.BadRequest && r.data) {
      let message = "";
      const data = r.data as ErrorResponse[];

      data.forEach((messageApi: any) => {
        message += messageApi.message + " \n";
      })

      this.alertWarningMessage(message);
      return r.success;
    }
    return r.success;
  }

  private async sendCompanyNfceInformation(companyNfceInformation: CompanyNfceInformation, tokenOverride?: string): Promise<boolean> {
    const r = await this.api.post<NfceInformation>('/v1/nfce/environment-information', companyNfceInformation, tokenOverride);
    if (r.statusCode == RequestResultStatusCodeEnum.BadRequest && r.data) {
      let message = "";
      const data = r.data as ErrorResponse[];

      data.forEach((messageApi: any) => {
        message += messageApi.message + " \n";
      })

      this.alertWarningMessage(message);
      return r.success;
    }
    return r.success;
  }

  private async sendNfceInformationOutOfWizard(pdvNfceInformation: PdvNfceInformation, tokenOverride?: string): Promise<boolean> {
    const r = await this.api.post<NfceInformation>('/v1/nfce/pdv-information', pdvNfceInformation, tokenOverride);
    if (r.statusCode == RequestResultStatusCodeEnum.BadRequest && r.data) {
      let message = "";
      const data = r.data as ErrorResponse[];

      data.forEach((messageApi: ErrorResponse) => {
        message += messageApi.message + " \n";
      })

      this.alertWarningMessage(message);
      return r.success;
    }
    return r.success;
  }

  async getNfce(): Promise<Nfce> {
    const r = await this.api.get<Nfce>('/v1/nfce/nfce-wizard');
    if (!r) this.alertWarningMessage("Erro ao recuperar informações do Wizard");
    if (!r.success && r.data) this.alertWarningMessage(r.data.toString());
    this._nfce = new Nfce(r.data as Nfce);
    this._isAccountantDetailSet.next(true);
    return this._nfce;
  }

  async wizardFinished(): Promise<boolean> {
    const r = await this.api.get<boolean>('/v1/nfce/nfce-wizard-check');
    if (!r) this.alertWarningMessage("Erro ao recuperar informações do Wizard");
    if (!r.success && r.data)this.alertWarningMessage(r.data.toString());
    return r.data as boolean;
  }

  async removeAccountant(accountantEmail: string) {
    const r = await this.api.post<boolean>('/v1/nfce/remove-relation-accountant', { Email: accountantEmail });
    if (!r) this.alertWarningMessage("Erro ao remover vínculo com o contador");
    if (!r.success && r.data) this.alertWarningMessage(r.data.toString());
    return r.data as boolean;
  }

  private alertWarningMessage(message: string) {
    this.snackBar.open(message, '', {
      duration: 5000,
      verticalPosition: 'top',
      panelClass: ['warning-snackbar']
    });
  }

  public async getNumerationNfce(serie: number): Promise<number> {
    const r = await this.api.get<number>("/v1/nfce/serie-nfce", { Serie: serie });
    if (r.success)
      return r.data

    return 0;
  }

  public async fetchStatesRequiringCsc(): Promise<string[]> {
    try {
      const response = await this.api.get<string | ErrorResponse[]>(
        "/admin/configuration/nfce-states-requiring-csc"
      );
      
      const data = response.data;
      if (typeof data !== 'string') {
        console.error('Resposta inesperada da API', data);
        return [];
      }

      return data.split(';').map((uf: string) => uf.trim().toUpperCase());
    } catch (error) {
      console.error('Erro ao carregar estados CSC', error);
      return [];
    }
  }
}

